import React, {
  useState,
  useEffect,
  PropsWithChildren,
  SetStateAction,
} from 'react';
import types, {
  JobOwnerSummaryViewType,
  ProductOrderViewType,
} from '../types/services-api';
import agent from '../agent';
import { navigate } from 'gatsby';
import { toJobPostingEdit, toJobSlotEdit } from '../routes';
import { displayStatus, getPosition } from '../utils/displayHelper';
import dayjs from 'dayjs';
import _orderBy from 'lodash/orderBy';
import { Many } from 'lodash';

export const initialFilters: JobPostingFiltersType = {
  keyword: '',
  startDate: '',
  endDate: '',
  location: '',
  status: '',
};

export const initialSort: JobPostingSortType = {
  formattedList: [],
  keys: ['isActiveStatus', 'defaultSortTime', 'id'],
  order: ['desc', 'desc', 'desc'],
};

const toTime = (date: string) => (date ? dayjs(date).valueOf() || 0 : 0);

// Active jobs first by most recent posted date, then everything else by
// latest expire date. Active subscription jobs get a far-future expire date
// so the Expire Date column sort keeps them at the top.
export const defaultJobPostingSort = (list: JobOwnerSummaryViewType[]) =>
  _orderBy(
    list.map((job) => {
      const adjustedExpireDate: any =
        job.isSubscription &&
        job.slotId > 0 &&
        !job.isUnslotted &&
        !job.isExpired
          ? dayjs().add(12, 'month').format('YYYY-MM-DD')
          : job.expiredDate;
      const isActiveStatus = displayStatus(job) === 'Active';
      const defaultSortTime = toTime(
        isActiveStatus ? job.postedDate : adjustedExpireDate
      );
      return { ...job, adjustedExpireDate, isActiveStatus, defaultSortTime };
    }),
    initialSort.keys,
    initialSort.order
  );

type SimpleAddressType = {
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
};

export type JobPostingContextType = {
  activeJobs?: types.JobOwnerSummaryViewType[];
  drafts?: types.JobOwnerSummaryViewType[];
  expiredPosts?: types.JobOwnerSummaryViewType[];
  closedActivePosts?: types.JobOwnerSummaryViewType[];
  credits?: types.JobOwnerSummaryViewType[];
  filteredPosts: types.JobOwnerSummaryViewType[];
  sortedPosts?: types.JobOwnerSummaryViewType[] | undefined;
  sort: JobPostingSortType;
  sortList: (newSort: JobPostingSortType) => void;
  filters: JobPostingFiltersType;
  setFilters: React.Dispatch<SetStateAction<JobPostingFiltersType>>;
  addressSet: {
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  }[];
  jobPostingsList: types.JobOwnerSummaryViewType[] | undefined;
  subscription?: ProductOrderViewType;
  subscriptionIsLoading: boolean;
  unassignedSlots: types.JobSlotType[];
  jobSlotList: types.JobSlotType[] | undefined;
  refreshJobLists: () => void;
  refreshSubscription: () => void;
  updateSubscription: (values: Partial<ProductOrderViewType>) => void;
  updateJobPostingsList: (values: Partial<JobOwnerSummaryViewType>) => void;
};

export const JobPostingContext = React.createContext<JobPostingContextType>({
  activeJobs: [],
  drafts: [],
  expiredPosts: [],
  closedActivePosts: [],
  credits: [],
  filteredPosts: [],
  sortedPosts: undefined,
  sort: initialSort,
  sortList: () => {},
  filters: initialFilters,
  setFilters: () => {},
  addressSet: [],
  jobPostingsList: undefined,
  subscription: undefined,
  subscriptionIsLoading: true,
  unassignedSlots: [],
  jobSlotList: undefined,
  refreshJobLists: () => {},
  refreshSubscription: () => {},
  updateSubscription: () => {},
  updateJobPostingsList: () => {},
});

export type ResumeSearchFiltersType = {
  keyword: string;
  type?: string;
  evaluation: string;
  photo?: boolean;
  disc?: boolean;
  values?: boolean;
  skills?: boolean;
  culture?: boolean;
  dow: string;
  radius: string;
  limit: number;
  offset: number;
  state: boolean;
  invited: boolean;
};

export interface ResumeSearchParametersType extends ResumeSearchFiltersType {
  position: string;
  zip: string;
}

export type JobPostingSortType = {
  formattedList: (JobOwnerSummaryViewType & { customColumn?: any })[];
  keys: string[];
  order: Many<boolean | 'desc' | 'asc'> | undefined;
};

export type JobPostingFiltersType = {
  keyword: string;
  startDate: string;
  endDate: string;
  location: string;
  status: string;
  isSubscription?: boolean;
};

export const DEFAULT_RESUME_SEARCH_FILTERS: ResumeSearchFiltersType = {
  dow: '0000000',
  radius: '200',
  limit: 25,
  offset: 0,
  state: false,
  evaluation: '',
  invited: false,
  keyword: '',
};

export function formatAddressKey(obj: SimpleAddressType) {
  return `${obj.address} ${obj.address2}, ${obj.city} ${obj.state}, ${obj.zip}`;
}

const JobPostingContextProvider = (props: PropsWithChildren) => {
  const [summarizedJobHistory, setSummarizedJobHistory] = useState<
    | {
        expiredPosts: JobOwnerSummaryViewType[];
        closedActivePosts: JobOwnerSummaryViewType[];
        activePosts: JobOwnerSummaryViewType[];
        draftPosts: JobOwnerSummaryViewType[];
        creditPosts: JobOwnerSummaryViewType[];
      }
    | undefined
  >(undefined);
  const [sort, setSort] = useState<JobPostingSortType>(initialSort);
  const [sortedPosts, setSortedPosts] = useState<
    JobOwnerSummaryViewType[] | undefined
  >(undefined);
  const [filters, setFilters] = useState<JobPostingFiltersType>(initialFilters);
  const [filteredPosts, setFilteredPosts] = useState<JobOwnerSummaryViewType[]>(
    []
  );
  const [addressSet, setAddressSet] = useState<SimpleAddressType[]>([]);
  const [jobPostingsList, setJobPostingsList] = useState<
    JobOwnerSummaryViewType[] | undefined
  >(undefined);
  const [jobSlotList, setJobSlotList] = useState<types.JobSlotType[]>();
  const [subscriptionIsLoading, setSubscriptionIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<ProductOrderViewType>();
  const [unassignedSlots, setUnassignedSlots] = useState<types.JobSlotType[]>(
    []
  );
  const [activeCanceledSlots, setActiveCanceledSlots] = useState<number[]>();
  const [activeCanceledSubscriptions, setActiveCanceledSubscriptions] =
    useState<ProductOrderViewType[]>();
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    fetchJobPostings();
  }, []);

  useEffect(() => {
    if (!subscription && subscriptionIsLoading) {
      fetchJobSlots();
    }
  }, [!!subscription, subscriptionIsLoading]);

  useEffect(() => {
    if (!!activeCanceledSlots) {
      if (activeCanceledSlots.length === 0) {
        setSubscriptionIsLoading(false);
      } else {
        fetchActiveCanceledSubscriptions(activeCanceledSlots);
      }
    }
  }, [activeCanceledSlots]);

  useEffect(() => {
    if (
      !!subscription &&
      !!activeCanceledSubscriptions &&
      activeCanceledSubscriptions.length > 0 &&
      activeCanceledSubscriptions.length === activeCanceledSlots?.length
    ) {
      const activeCanceledInviteQuantity = activeCanceledSubscriptions.reduce(
        (acc, sub) => {
          return acc + sub.inviteQuantity;
        },
        0
      );
      const activeCanceledInviteQuantityIncluded =
        activeCanceledSubscriptions.reduce((acc, sub) => {
          return acc + sub.inviteQuantityIncluded;
        }, 0);
      const activeCanceledInviteDebits = activeCanceledSubscriptions.reduce(
        (acc, sub) => {
          return acc + sub.inviteDebits;
        },
        0
      );
      updateSubscription({
        inviteQuantity:
          subscription.inviteQuantity + activeCanceledInviteQuantity,
        inviteQuantityIncluded:
          subscription.inviteQuantityIncluded +
          activeCanceledInviteQuantityIncluded,
        inviteDebits: subscription.inviteDebits + activeCanceledInviteDebits,
      });
    }
  }, [!!subscription, activeCanceledSubscriptions]);

  useEffect(() => {
    if (!!activeCanceledSubscriptions && !!subscription) {
      setSubscriptionIsLoading(false);
    }
  }, [activeCanceledSubscriptions, subscription]);

  useEffect(() => {
    if (
      !!jobPostingsList &&
      !!summarizedJobHistory &&
      jobPostingsList.length > 0
    ) {
      if (filters !== initialFilters) {
        const updatedFilteredList = jobPostingsList.filter((job) => {
          const searchTextLowercase = filters.keyword.toLowerCase();
          const keywordFilter =
            `${job.id}`.includes(searchTextLowercase) ||
            (job.city &&
              job.city.toLowerCase().includes(searchTextLowercase)) ||
            (job.state &&
              job.state.toLowerCase().includes(searchTextLowercase)) ||
            (job.zip && job.zip.includes(searchTextLowercase)) ||
            getPosition(job.position)
              .toLowerCase()
              .includes(searchTextLowercase) ||
            (job.name &&
              job.name.toLowerCase().includes(searchTextLowercase)) ||
            (job.description &&
              job.description.toLowerCase().includes(searchTextLowercase)) ||
            (job.isPlanned && 'temp'.includes(searchTextLowercase)) ||
            (job.isBasic && 'basic'.includes(searchTextLowercase)) ||
            (!job.isBasic && 'premium'.includes(searchTextLowercase)) ||
            (job.isSubscription &&
              'subscription'.includes(searchTextLowercase));

          const dateFilter =
            !!filters.startDate && !!filters.endDate
              ? dayjs(job.postedDate).isAfter(dayjs(filters.startDate)) &&
                dayjs(job.postedDate).isBefore(dayjs(filters.endDate))
              : !!filters.startDate
                ? dayjs(job.postedDate).isAfter(dayjs(filters.startDate))
                : !!filters.endDate
                  ? dayjs(job.postedDate).isBefore(dayjs(filters.endDate))
                  : true;
          const locationFilter = !!filters.location
            ? `${formatAddressKey(job)}` === filters.location
            : true;
          const statusFilter = !!filters.status
            ? filterByStatus(job, filters.status)
            : true;
          const subscriptionFilter = filters.isSubscription
            ? job.isSubscription
            : true;
          return (
            keywordFilter &&
            dateFilter &&
            locationFilter &&
            statusFilter &&
            subscriptionFilter
          );
        });
        setFilteredPosts(updatedFilteredList);
        setSortedPosts(updatedFilteredList);
        sortList({ ...initialSort, formattedList: updatedFilteredList });
      } else {
        setFilteredPosts(jobPostingsList);
        setSortedPosts(jobPostingsList);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (sort.formattedList.length > 0) {
      const updatedSort: (types.JobOwnerSummaryViewType & {
        customColumn?: any;
      })[] = _orderBy(sort.formattedList, sort.keys, sort.order);
      setSortedPosts(updatedSort);
    }
  }, [sort]);

  function filterByStatus(job: JobOwnerSummaryViewType, status: string) {
    switch (status) {
      case 'Draft':
        return job.status === 'DRAFT' && !!job.position;
      case 'Filled':
        return job.status === 'BOOKED';
      case 'Closed':
        return (
          job.status === 'CANCELED' ||
          (job.isUnslotted && job.status !== 'BOOKED')
        );
      case 'Expired':
        return job.isExpired && job.status === 'ACTIVE';
      case 'Active':
        return job.isActive;
      default:
        return false;
    }
  }

  function sortList(props: {
    formattedList: types.JobOwnerSummaryViewType[];
    keys: string[];
    order: Many<boolean | 'desc' | 'asc'> | undefined;
  }) {
    setSort(props);
  }

  function fetchJobPostings(navigateToLatest?: boolean) {
    agent.Employer.jobs()
      .then((res) => {
        const nonCreditJobs: JobOwnerSummaryViewType[] = defaultJobPostingSort(
          res.data.filter((job) => !!job.position)
        );
        const addresses: SimpleAddressType[] = [
          ...new Map(
            nonCreditJobs.map((obj) => [
              `${formatAddressKey(obj)}`,
              {
                address: obj.address,
                address2: obj.address2,
                city: obj.city,
                state: obj.state,
                zip: obj.zip,
              },
            ])
          ).values(),
        ];
        setAddressSet(addresses);
        setSortedPosts(nonCreditJobs);
        const categorizedPosts = res.data.reduce<{
          expiredPosts: JobOwnerSummaryViewType[];
          closedActivePosts: JobOwnerSummaryViewType[];
          activePosts: JobOwnerSummaryViewType[];
          draftPosts: JobOwnerSummaryViewType[];
          creditPosts: JobOwnerSummaryViewType[];
        }>(
          (obj, item) => {
            const categorizedItems = {
              expiredPosts:
                item.isExpired || item.isUnslotted
                  ? [...obj['expiredPosts'], item]
                  : obj['expiredPosts'],
              closedActivePosts:
                !item.isExpired &&
                !item.isUnslotted &&
                (item.status === 'BOOKED' || item.status === 'CANCELED')
                  ? [...obj['closedActivePosts'], item]
                  : obj['closedActivePosts'],
              activePosts:
                !item.isExpired && !item.isUnslotted && item.status === 'ACTIVE'
                  ? [...obj['activePosts'], item]
                  : obj['activePosts'],
              draftPosts:
                item.status === 'DRAFT' && !!item.position
                  ? [...obj['draftPosts'], item]
                  : obj['draftPosts'],
              creditPosts:
                item.status === 'DRAFT' && !item.position
                  ? [...obj['creditPosts'], item]
                  : obj['creditPosts'],
            };
            return { ...categorizedItems };
          },
          {
            expiredPosts: [],
            closedActivePosts: [],
            activePosts: [],
            draftPosts: [],
            creditPosts: [],
          }
        );
        setSummarizedJobHistory(categorizedPosts);
        setJobPostingsList(nonCreditJobs);
        setFilteredPosts(nonCreditJobs);
        if (navigateToLatest) {
          navigate(toJobPostingEdit(categorizedPosts.creditPosts[0].id));
        }
      })
      .catch((err) => {
        // TODO: show a global, floating Alert?
        console.warn(err.message);
      });
  }

  function fetchJobSlots(navigateToLatest?: boolean) {
    setSubscriptionIsLoading(true);
    agent.Employer.jobSlots()
      .then((res) => {
        const response = res.data;
        const unassigned = response
          .filter((slot) => slot.activeJobId === 0)
          .sort((a, b) => (a.slotId > b.slotId ? 1 : -1));
        setJobSlotList(response);
        if (navigateToLatest) {
          navigate(toJobSlotEdit(response[0].slotId), {
            state: {
              selectedAction: 'CREATE-NEW',
            },
          });
        }
        if (response.length > 0) {
          fetchSubscription(response[0].orderId);
          setActiveCanceledSlots(
            response
              .filter(
                (slot, index) =>
                  index > 0 &&
                  !!slot.canceledDate &&
                  dayjs(slot.expireDate).isAfter(dayjs())
              )
              .reduce((acc, slot) => {
                if (!acc.some((s) => s === slot.orderId)) {
                  return [...acc, slot.orderId];
                }
                return acc;
              }, [] as number[])
          );
        } else {
          setSubscriptionIsLoading(false);
        }
        setUnassignedSlots(unassigned);
      })
      .catch((err) => {
        // TODO: show a global, floating Alert?
        console.warn(err.message);
      });
  }

  function fetchSubscription(orderId: number) {
    agent.Employer.subscription(orderId)
      .then((subRes) => {
        setSubscription(subRes.data);
      })
      .catch((err) => {
        setErrorMessage(err.message);
        setSubscriptionIsLoading(false);
      });
  }

  function fetchActiveCanceledSubscriptions(orderIds: number[]) {
    orderIds.forEach((orderId) => {
      agent.Employer.subscription(orderId)
        .then((subRes) => {
          setActiveCanceledSubscriptions((prev) => [
            ...(prev || []),
            subRes.data,
          ]);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setSubscriptionIsLoading(false);
        });
    });
  }

  function refreshJobLists() {
    fetchJobPostings();
  }

  function refreshSubscription() {
    setSubscription(undefined);
    setSubscriptionIsLoading(true);
    setActiveCanceledSlots(undefined);
    setActiveCanceledSubscriptions(undefined);
  }

  function updateJobPostingsList(values: Partial<JobOwnerSummaryViewType>) {
    setSortedPosts(
      sortedPosts?.map((obj) => {
        if (obj.id === values.id) {
          return { ...obj, ...values };
        } else {
          return obj;
        }
      })
    );
  }

  function updateSubscription(values: Partial<ProductOrderViewType>) {
    if (!!subscription) {
      setSubscription({ ...subscription, ...values });
    }
  }

  JobPostingContext.displayName = 'Job Posting Context';

  return (
    <JobPostingContext.Provider
      value={{
        activeJobs: summarizedJobHistory?.activePosts,
        drafts: summarizedJobHistory?.draftPosts,
        expiredPosts: summarizedJobHistory?.expiredPosts,
        closedActivePosts: summarizedJobHistory?.closedActivePosts,
        credits: summarizedJobHistory?.creditPosts,
        sortedPosts,
        sort,
        sortList,
        filteredPosts,
        filters,
        setFilters,
        addressSet,
        jobPostingsList,
        unassignedSlots,
        jobSlotList,
        refreshJobLists,
        refreshSubscription,
        subscription,
        subscriptionIsLoading,
        updateSubscription,
        updateJobPostingsList,
      }}
    >
      {props.children}
    </JobPostingContext.Provider>
  );
};

export default JobPostingContextProvider;
